/**
 * Pure conversion functions for mapping between server treatment data,
 * form state, and API request shapes.
 */
import { addMonths, format } from 'date-fns'

import type {
  OrderWithAllocations,
  PrescribingContext,
  ProductWithStock,
  PutTreatmentBody,
  RegionId,
  TreatmentWithRelations,
} from '@snootsvet/shared'
import { calculateOrderTotal, resolveDateFormat, wallClockToUtc } from '@snootsvet/shared'

import type { TreatmentReminderTarget } from '@/frontend/features/patient/reminder-targets'

import type { FormTreatmentItem, SavedTreatmentItem, TreatmentCategory } from './treatments.types'

/**
 * Returns the snapshotted line price (in pence) for the original consultation-time order.
 * Returns null when no non-refill, non-cancelled order exists (e.g. external prescriptions,
 * which never generate orders).
 */
export function getTreatmentPrice(t: TreatmentWithRelations): number | null {
  return getPricedOrder(t)?.totalPrice ?? null
}

/**
 * The order a treatment's price comes from — the first non-refill, non-cancelled one.
 *
 * Prefer this over `getTreatmentPrice` when displaying money: the order carries both
 * the amount and the `clinicId` it was billed at (non-null in the DB), so formatting
 * via `formatClinicPrice(order.totalPrice, order.clinicId)` takes the currency from
 * the same row as the amount rather than inferring it from an enclosing record.
 */
export function getPricedOrder(t: TreatmentWithRelations): OrderWithAllocations | null {
  return t.orders.find((o) => !o.isRefill && o.status !== 'cancelled') ?? null
}

/**
 * Maps a treatment to its UI treatment category: services and vaccines each get their own
 * category (each has a dedicated form); medications and consumables share 'prescription'
 * (same Medication form).
 *
 * Mirrors `isVaccineTreatment` (type OR product) so a vaccination whose inventory product
 * doesn't resolve to `vaccine` still opens the Vaccine form — otherwise its reminders would
 * be silently dropped on edit (the Medication form emits no reminders).
 */
export function deriveTreatmentCategory(
  productType: string | null | undefined,
  treatmentType?: string | null
): Exclude<TreatmentCategory, 'external'> {
  if (treatmentType === 'vaccination' || productType === 'vaccine') return 'vaccination'
  if (productType === 'service') return 'service'
  return 'prescription'
}

/** Maps a server treatment to a `SavedTreatmentItem` for rendering. */
export function toSavedItem(
  t: TreatmentWithRelations & { product: ProductWithStock | null }
): SavedTreatmentItem {
  const order = t.orders[0] ?? null
  const editable = !t.isCommitted
  // Recover the zero-fee waiver from the snapshotted order discount so editing a
  // waived item doesn't silently re-apply the full charge (10000 bps = 100% off).
  const zeroFee = order?.discountBps === 10000

  const persisted = {
    state: 'saved' as const,
    id: t.id,
    createdAt: t.createdAt,
    totalPrice: getTreatmentPrice(t) ?? 0,
    subtotalPrice: order ? calculateOrderTotal({ ...order, discountBps: 0 }) : null,
    orderStatus: (order?.status as SavedTreatmentItem['orderStatus']) ?? null,
    orderPayment: order
      ? {
          paymentStatus: order.paymentStatus as SavedTreatmentItem['orderPayment'] extends null
            ? never
            : NonNullable<SavedTreatmentItem['orderPayment']>['paymentStatus'],
          totalPrice: order.totalPrice,
        }
      : null,
    editable,
  }

  if (t.isExternal) {
    return {
      ...persisted,
      type: 'external_prescription',
      treatmentCategory: 'external',
      catalogProductId: t.catalogProductId!,
      quantity: t.quantity,
      instructions: t.instructions ?? '',
      repeatsAllowed: t.repeatsAllowed ?? 0,
      validUntil: t.validUntil ? new Date(t.validUntil) : new Date(),
      veterinaryCascade: t.veterinaryCascade,
      clinicalNotes: t.clinicalNotes,
      internalNotes: t.internalNotes,
    }
  }

  const treatmentCategory = deriveTreatmentCategory(t.product?.type, t.treatmentType)

  if (t.administeredInConsult) {
    // Pre-populate allocations from the original order's active allocation rows so
    // the edit form shows which batches were used and lets the vet reassign them.
    const existingAllocations = (order?.allocations ?? [])
      .filter((a) => a.reversedAt === null)
      .map((a) => ({ batchNumber: a.stock.batchNumber, quantity: a.quantity }))

    return {
      ...persisted,
      type: 'internal_administered',
      treatmentCategory,
      productId: t.productId!,
      quantity: t.quantity,
      clinicId: order?.clinicId ?? null,
      allocations: existingAllocations,
      zeroFee,
      clinicalNotes: t.clinicalNotes,
      internalNotes: t.internalNotes,
      tagNumber: t.tagNumber,
      injectionSite: t.injectionSite,
      // Hydrate from the server's active reminders (cancelled ones aren't returned).
      // `?? []` guards data sources that don't load the relation — the field is
      // optional on TreatmentWithRelations for exactly that reason.
      reminders: t.reminders.map((r) => ({
        id: r.id,
        target: r.target as TreatmentReminderTarget,
        dueAt: r.dueAt,
      })),
      product: t.product,
    }
  }

  return {
    ...persisted,
    type: 'internal',
    treatmentCategory: 'prescription',
    productId: t.productId!,
    quantity: t.quantity,
    clinicId: order?.clinicId ?? null,
    instructions: t.instructions ?? '',
    repeatsAllowed: t.repeatsAllowed ?? 0,
    refillInterval: t.refillInterval,
    validUntil: t.validUntil ? new Date(t.validUntil) : null,
    urgency: (order?.urgency as 'low' | 'medium' | 'high') ?? 'medium',
    zeroFee,
    clinicalNotes: t.clinicalNotes,
    internalNotes: t.internalNotes,
    tagNumber: t.tagNumber,
    injectionSite: t.injectionSite,
    product: t.product,
  }
}

/** Maps a validated form item to the API shape for create or update. */
export function toApiItem(item: FormTreatmentItem, region: RegionId): PutTreatmentBody {
  const expiry = 'validUntil' in item ? item.validUntil : null
  const expiryFields = expiry
    ? {
        validUntil: wallClockToUtc(
          format(expiry, 'yyyy-MM-dd'),
          '23:59:59',
          resolveDateFormat('clinicId' in item ? item.clinicId : null, region).timeZone
        ).toISOString(),
      }
    : {}

  switch (item.type) {
    case 'internal':
      return {
        type: 'internal',
        productId: item.productId,
        clinicId: item.clinicId,
        quantity: item.quantity,
        discountBps: item.zeroFee ? 10000 : 0,
        instructions: item.instructions,
        repeatsAllowed: item.repeatsAllowed,
        refillInterval: item.repeatsAllowed > 0 ? item.refillInterval : null,
        ...expiryFields,
        urgency: item.urgency,
        clinicalNotes: item.clinicalNotes ?? undefined,
        internalNotes: item.internalNotes ?? undefined,
        // Trim on write so whitespace-only input is stored as absent, not " ".
        tagNumber: item.tagNumber?.trim() || undefined,
        injectionSite: item.injectionSite?.trim() || undefined,
      }
    case 'internal_administered':
      return {
        type: 'internal_administered',
        productId: item.productId,
        clinicId: item.clinicId,
        quantity: item.quantity,
        discountBps: item.zeroFee ? 10000 : 0,
        allocations: item.allocations,
        clinicalNotes: item.clinicalNotes ?? undefined,
        internalNotes: item.internalNotes ?? undefined,
        // Trim on write so whitespace-only input is stored as absent, not " ".
        tagNumber: item.tagNumber?.trim() || undefined,
        injectionSite: item.injectionSite?.trim() || undefined,
        reminders:
          item.reminders.length > 0
            ? item.reminders.map((r) => ({ id: r.id, target: r.target, dueAt: r.dueAt }))
            : undefined,
      }
    case 'external_prescription':
      return {
        type: 'external_prescription',
        catalogProductId: item.catalogProductId,
        quantity: item.quantity,
        discountBps: 0,
        instructions: item.instructions,
        repeatsAllowed: item.repeatsAllowed,
        ...expiryFields,
        veterinaryCascade: item.veterinaryCascade,
        clinicalNotes: item.clinicalNotes ?? undefined,
        internalNotes: item.internalNotes ?? undefined,
      }
    case 'external_prescription_with_task':
      return {
        type: 'external_prescription_with_task',
        catalogProductId: item.catalogProductId,
        quantity: item.quantity,
        discountBps: 0,
        instructions: item.instructions,
        repeatsAllowed: item.repeatsAllowed,
        ...expiryFields,
        veterinaryCascade: item.veterinaryCascade,
        clinicId: item.clinicId,
        clinicalNotes: item.clinicalNotes ?? undefined,
        internalNotes: item.internalNotes ?? undefined,
      }
  }
}

/**
 * Converts an existing treatment into a FormTreatmentItem for pre-populating
 * the editor when re-prescribing. External prescriptions get a fresh 6-month
 * validUntil; internal prescriptions default to medium urgency.
 */
export function toReprescribeItem(
  rx: TreatmentWithRelations,
  context: PrescribingContext = 'standalone'
): FormTreatmentItem {
  const sixMonthsFromNow = addMonths(new Date(), 6)

  if (rx.isExternal) {
    return {
      type: 'external_prescription',
      treatmentCategory: 'external',
      catalogProductId: rx.catalogProductId!,
      quantity: rx.quantity,
      instructions: rx.instructions ?? '',
      repeatsAllowed: rx.repeatsAllowed ?? 0,
      validUntil: sixMonthsFromNow,
      veterinaryCascade: rx.veterinaryCascade,
      clinicalNotes: rx.clinicalNotes,
      internalNotes: rx.internalNotes,
    }
  }

  if (rx.administeredInConsult && context === 'clinic_consultation') {
    return {
      type: 'internal_administered',
      // Classify the same way toSavedItem does so it opens under the right tab.
      treatmentCategory: deriveTreatmentCategory(rx.product?.type, rx.treatmentType),
      productId: rx.productId!,
      quantity: rx.quantity,
      clinicId: rx.orders[0]?.clinicId ?? '',
      allocations: [],
      // Re-prescribe starts a fresh order — the vet re-decides any fee waiver.
      zeroFee: false,
      clinicalNotes: rx.clinicalNotes,
      internalNotes: rx.internalNotes,
      // Re-prescribing starts fresh — the tag/site were specific to the original
      // administration and don't carry over.
      tagNumber: null,
      injectionSite: null,
      // Re-prescribing starts fresh — new reminders are computed from the fresh
      // administration date, not carried over from the original.
      reminders: [],
      product: rx.product ? { ...rx.product, stock: [] } : null,
    }
  }

  return {
    type: 'internal',
    treatmentCategory: 'prescription',
    productId: rx.productId!,
    quantity: rx.quantity,
    clinicId: rx.orders[0]?.clinicId ?? '',
    instructions: rx.instructions ?? '',
    repeatsAllowed: rx.repeatsAllowed ?? 0,
    refillInterval: rx.refillInterval,
    urgency: 'medium',
    zeroFee: false,
    clinicalNotes: rx.clinicalNotes,
    internalNotes: rx.internalNotes,
    tagNumber: null,
    injectionSite: null,
    product: rx.product ? { ...rx.product, stock: [] } : null,
  }
}
